import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { useCallback, useEffect, useState } from 'react';
import { SERVER_HOST } from './environment';
let keycloak: Keycloak;

export function useKeycloakAuth(
  authUrl?: string,
  realm?: string,
  client?: string
): {
  loggedIn: boolean;
  logout: () => void;
  errorMessage: string | null;
  getProfile: () => Promise<KeycloakProfile>;
} {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const invalidConfigurationMessage = 'Invalid system parameters were provided.';
  const missingRoleMessage = 'We cannot find your account.';
  const systemErrorMessage = 'We have encountered a system error: $1.';

  const isAuthorized = (keyCloakClient: Keycloak) => {
    if (!keyCloakClient.clientId) {
      return false;
    }
    return (
      keyCloakClient.tokenParsed?.realm_access?.roles.includes(keyCloakClient.clientId) ?? false
    );
  };

  const getProfile = useCallback(async () => {
    if (!loggedIn) {
      throw new Error('Not Logged In');
    }
    const profile = await keycloak.loadUserProfile();
    return profile;
  }, [loggedIn]);

  let refreshIntervalId: NodeJS.Timer;

const postTokenToVertex = (token) =>{
  return fetch(`${SERVER_HOST}/api/token-to-cookie`, {
    method: 'POST',
    body: token,
    headers: { 'Content-Type': 'text/plain' },
    credentials: 'include'
  })
  .then(res => {
    if (!res.ok) throw new Error('Failed to set secure cookie');
    return res.text();
  })
  .catch(err => {
    console.error('Error setting cookie:', err);
    setLoggedIn(false);
    setErrorMessage('Login failed: could not store token securely.');
  });
}
   

  useEffect(() => {
  const refreshToken = (kc: Keycloak) => {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
    }
    const refresh = () => {
      kc.updateToken(30)
        .then(() => {
          if (isAuthorized(kc)) {
            postTokenToVertex(kc.token)
              .then(() => {
                setLoggedIn(true); 
              });
          } else {
            setLoggedIn(false);
            setErrorMessage(missingRoleMessage);
          }
        })
        .catch((e: Error | undefined) => {
          if (e === undefined) {
            e = new Error('Undefined Error');
          }
          console.error('Failed to refresh keycloak token', e);
          setLoggedIn(false);
          setErrorMessage(systemErrorMessage.replace('$1', e.message));
        });
    };
    refresh();

    refreshIntervalId = setInterval(() => {
      refresh();
    }, 30000);
  };

    if (!keycloak) {
      let kc: Keycloak;
      if (!authUrl) {
        kc = new Keycloak('/keycloak.json');
      } else if (authUrl.includes('/keycloak.json')) {
        kc = new Keycloak(authUrl);
      } else {
        if (!client || !realm) {
          setErrorMessage(invalidConfigurationMessage);
          return;
        }
        kc = new Keycloak({
          url: authUrl,
          clientId: client,
          realm: realm,
        });
      }

      kc.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
        .then(function (authenticated: boolean) {
          if (authenticated) {
            refreshToken(kc);
          } else {
            setLoggedIn(false);
            setErrorMessage('Login failed');
          }
        })
        .catch(function (e: Error | undefined) {
          if (e === undefined) {
            e = new Error('Undefined Error');
          }
          console.error('Failed to authenticate keycloak', e);
          setLoggedIn(false);
          setErrorMessage(systemErrorMessage.replace('$1', e.message));
        });
      keycloak = kc;
    } else if (keycloak.token) {
      if (isAuthorized(keycloak)) {
         postTokenToVertex(keycloak.token)
         .then(() => {
          setLoggedIn(true); 
        });
        
      } else {
        setLoggedIn(false);
        setErrorMessage(missingRoleMessage);
      }
    }
  }, [authUrl, client, realm ]);

  return { loggedIn, logout: () => keycloak?.logout(), errorMessage, getProfile };
}



export function getKeycloakAuthHeaders() {
  if (!keycloak || !keycloak.token) {
    throw new Error('Not Logged In');
  }
  return {
    'X-Authorization': keycloak.token,
  };
}
